import { useAnalytics, withEventContext } from "@codat/analytics";
import { FileItem, Typography } from "@codat/orchard-ui";

import { fileNameToId } from "./file-name-to-id";
import styles from "./FileUpload.module.css";
import { FileUploadState, getMessage } from ".";

type FileItemState = "loading" | "success" | "error" | undefined;

interface FileListDisplayProps {
  current: FileUploadState[];
  maxFiles: number;
  onDelete: (file: File) => void;
}

const toFileItemState = (fileItem: FileUploadState): FileItemState => {
  switch (fileItem.state.kind) {
    case "ready":
      return undefined;
    case "uploading":
      return "loading";
    case "uploaded":
      return "success";
    case "error":
    case "upload-error":
      return "error";
  }
};

const FileListDisplayContent: React.FC<FileListDisplayProps> = (
  props: FileListDisplayProps
) => {
  const { current, maxFiles, onDelete } = props;

  const analytics = useAnalytics();

  const getOnDelete = (fileItem: FileUploadState): (() => void) | undefined => {
    if (
      fileItem.state.kind === "uploaded" ||
      fileItem.state.kind === "uploading"
    ) {
      return undefined;
    } else {
      return () => {
        analytics.sendEvent("File deleted");
        onDelete(fileItem.file);
      };
    }
  };

  return current.length > 0 ? (
    <div data-testid="file-list-display">
      <div className={styles.fileCountIndicator}>
        <Typography as="p">
          <strong>Files</strong>
        </Typography>
        <Typography as="p">•</Typography>
        <Typography as="p">
          {current.length}/{maxFiles}
        </Typography>
      </div>
      <div className={styles.fileList}>
        {current.map((c) => (
          <FileItem
            key={c.file.name}
            id={"file-item-" + fileNameToId(c.file)}
            file={c.file}
            state={toFileItemState(c)}
            errorText={getMessage(c)}
            onDeleteClick={getOnDelete(c)}
          />
        ))}
      </div>
    </div>
  ) : (
    <></>
  );
};

export const FileListDisplay = withEventContext<
  FileListDisplayProps & JSX.IntrinsicAttributes
>(FileListDisplayContent, "File list");
